import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/Controls/Checkbox";
import { Input } from "~/components/Controls/Input";
import { useConfirm, useMessage } from "~/components/Dialogs";
import { IntentText } from "~/components/Form";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import { clientsLinks } from "~/modules/Clients";
import { createDspConnectionType, deleteDspConnectionType, updateDspConnectionType } from "~/modules/Dictionary/api";
import { DictionaryUsageLinks } from "~/modules/Dictionary/components/DictionaryUsageLinks";
import { DSP_CONNECTION_TYPES_QUERY_KEY, useDspConnectionTypesQuery } from "~/modules/Dictionary/queries";
import { IDspConnectionType } from "~/modules/Dictionary/models/DspConnectionType";
import style from "./DspConnectionTypesTab.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

type FormMode = "idle" | "create" | "edit";

export const DspConnectionTypesTab = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const message = useMessage();

  const { data: items = [], isPending, isFetching, isError, error, refetch } = useDspConnectionTypesQuery();

  const [mode, setMode] = useState<FormMode>("idle");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [netsuiteId, setNetsuiteId] = useState("");
  const [modifyPubId, setModifyPubId] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createDspConnectionType({
        name: name.trim(),
        label: label.trim(),
        netsuiteId: netsuiteId.trim() || undefined,
        modifyPubId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DSP_CONNECTION_TYPES_QUERY_KEY });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateDspConnectionType(editingId!, {
        label: label.trim(),
        netsuiteId: netsuiteId.trim() || undefined,
        modifyPubId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DSP_CONNECTION_TYPES_QUERY_KEY });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteDspConnectionType(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DSP_CONNECTION_TYPES_QUERY_KEY });
    },
  });

  const normalizedItems = useMemo(() => items ?? [], [items]);
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isFormOpen = mode !== "idle";
  const isEditing = mode === "edit";
  const canSubmit = name.trim().length > 0 && label.trim().length > 0;

  const resetForm = () => {
    setFormError(null);
    setName("");
    setLabel("");
    setNetsuiteId("");
    setModifyPubId(false);
    setEditingId(null);
    setMode("idle");
  };

  const handleOpenAdd = () => {
    setFormError(null);
    setName("");
    setLabel("");
    setNetsuiteId("");
    setModifyPubId(false);
    setEditingId(null);
    setMode("create");
  };

  const handleEdit = (item: IDspConnectionType) => {
    setFormError(null);
    setName(item.name);
    setLabel(item.label);
    setNetsuiteId(item.netsuiteId ?? "");
    setModifyPubId(item.modifyPubId);
    setEditingId(item.id);
    setMode("edit");
  };

  const handleCancel = () => resetForm();

  const handleSave = () => {
    setFormError(null);
    const mutation = isEditing ? updateMutation : createMutation;
    mutation.mutate(undefined, {
      onError: (e: any) => {
        const msg = getErrorMessage(e);
        setFormError(msg);
        message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
      },
    });
  };

  const handleDelete = (item: IDspConnectionType) =>
    confirm(`Delete DSP connection type "${item.label}"?`, {
      icon: <i className="fa fa-question" />,
      intent: Intent.DANGER,
      onOk: () => {
        deleteMutation.mutate(item.id, {
          onError: (e: any) => {
            const status = e?.response?.status;
            if (status === 409) {
              message("This connection type is used by an Agency and cannot be deleted.", {
                intent: Intent.DANGER,
                icon: <i className="fa fa-times" />,
              });
              return;
            }
            if (status === 404) {
              message("Not found.", { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
              return;
            }
            message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        });
      },
    });

  return (
    <div>
      <div className={style.toolbar}>
        <div className={style.toolbarLeft}>
          {isFormOpen && (
            <div className={style.formRowFour}>
              <Input
                className={style.field}
                placeholder="Name"
                value={name}
                onChange={setName}
                disabled={isBusy || isEditing}
                autoFocus
              />
              <Input
                className={style.field}
                placeholder="Label"
                value={label}
                onChange={setLabel}
                disabled={isBusy}
              />
              <Input
                className={style.field}
                placeholder="NetSuite ID"
                value={netsuiteId}
                onChange={setNetsuiteId}
                disabled={isBusy}
              />
              <CheckboxInput
                value={modifyPubId}
                onChange={setModifyPubId}
                disabled={isBusy}
                label="Modify Pub ID"
              />
            </div>
          )}
          {!isFormOpen && (
            <DictionaryUsageLinks
              label="Name is used in rollout targets for"
              showColon={false}
              links={[{ label: "Agencies", to: clientsLinks.agencies() }]}
            />
          )}
        </div>
        <div className={style.toolbarRight}>
          {!isFormOpen ? (
            <Button
              responsive
              intent={Intent.PRIMARY}
              disabled={isBusy}
              onClick={handleOpenAdd}
              leftIcon={<i className="fa fa-plus" />}
            >
              Add
            </Button>
          ) : (
            <>
              <Button
                responsive
                intent={Intent.PRIMARY}
                disabled={!canSubmit || isBusy}
                onClick={handleSave}
                leftIcon={<i className="fa fa-check" />}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
              <Button
                responsive
                disabled={isBusy}
                onClick={handleCancel}
                leftIcon={<i className="fa fa-times" />}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            responsive
            intent={Intent.SUCCESS}
            disabled={isFetching}
            onClick={() => refetch()}
            leftIcon={<i className="fa fa-refresh" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {isFormOpen && formError && <IntentText className={style.error} intent={Intent.DANGER}>{formError}</IntentText>}
      {isError && <IntentText className={style.error} intent={Intent.DANGER}>{getErrorMessage(error)}</IntentText>}

      <div className={style.tableWrap}>
        {isPending && normalizedItems.length === 0 && <Progress message="Loading..." />}
        {!isPending && normalizedItems.length === 0 && (
          <div className={style.emptyState}>No DSP connection types yet.</div>
        )}
        {normalizedItems.length > 0 && (
          <TableSort
            records={normalizedItems}
            header
            columns={[
              { header: "Name", field: "name", sort: sortByString },
              { header: "Label", field: "label", sort: sortByString },
              { header: "NetSuite ID", field: "netsuiteId", sort: sortByString, cellAttrs: { className: style.netsuiteIdCell } },
              { header: "Modify Pub ID", cell: (item: IDspConnectionType) => item.modifyPubId ? <i className="fas fa-check" /> : null },
              {
                short: true,
                cell: (item: IDspConnectionType) => (
                  <TableRowActions>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.PRIMARY}
                      disabled={isBusy}
                      onClick={() => handleEdit(item)}
                      leftIcon={<i className="fa fa-edit" />}
                    >
                      Edit
                    </Button>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.DANGER}
                      disabled={isBusy}
                      onClick={() => handleDelete(item)}
                      leftIcon={<i className="fa fa-trash" />}
                    >
                      Delete
                    </Button>
                  </TableRowActions>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
